import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { SitterReview, SitterReviewService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { SitterProfileChrome } from '../SitterReviews/components/SitterProfileChrome';

const MAX_LEN = 2500;

const formatDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

export const SitterReviewReply = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: sitterIdParam, reviewId } = useParams<{ id: string; reviewId: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [review, setReview] = useState<SitterReview | null>(null);
  const [reply, setReply] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Respond to Review';
  }, []);

  useEffect(() => {
    const id = Number(reviewId);
    if (!user?.id || !id) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterReviewService.get(id, { params: { userId: user.id, sitterId: user.id } });
        const r = resp.data?.data ?? null;
        setReview(r);
        setReply(r?.reply ?? '');
      } catch (err) {
        console.warn('[SitterReviewReply] load failed', err);
      }
    };
    void load();
  }, [reviewId, user?.id]);

  const sitterId = Number(sitterIdParam) || review?.sitter?.id;
  const remaining = MAX_LEN - reply.length - (reply.match(/\n/g) || []).length;
  const dateStr = formatDate(review?.date ?? review?.created);
  const editedDate = review?.updated && formatDate(review.updated);
  const edited = editedDate && editedDate !== dateStr;

  const submit = async (): Promise<void> => {
    if (!review?.id) return;
    setSaving(true);
    try {
      await SitterReviewService.patch(review.id, { ...review, reply });
      navigate(`/sitter/${sitterId}/reviews`);
    } catch (err) {
      console.warn('[SitterReviewReply] submit failed', err);
      toast.error(t('pages.sitterReviewReply.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!review) {
    return <div className='container py-6' />;
  }

  return (
    <div className='container py-6 max-w-3xl mx-auto'>
      <SitterProfileChrome
        sitter={review.sitter ?? null}
        activeTab='reviews'
        viewerUserId={user?.id}
      />
      <h2 className='text-xl font-semibold'>{t('pages.sitterReviewReply.heading')}</h2>
      <hr className='my-3' />
      <div className='flex items-start gap-3'>
        <a href={`/app/parent/${review.parent?.id ?? ''}`}>
          <img
            src={review.parent?.pictureUrl}
            alt=''
            className='w-20 h-20 rounded object-cover'
          />
        </a>
        <div className='flex-1'>
          <div className='flex gap-0.5'>
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className={i < (review.rating ?? 0) ? 'text-amber-400' : 'text-neutral-300'}>★</span>
            ))}
          </div>
          <div className='text-sm font-semibold mt-1'>
            {t('pages.sitterReviewReply.reviewedOn', { date: dateStr })}
            {edited && (
              <span className='font-normal text-neutral-600'>
                {' '}{t('pages.sitterReviewReply.editedOn', { date: editedDate })}
              </span>
            )}
          </div>
          {review.title && <p className='mt-2 font-semibold'>{review.title}</p>}
          {review.comment && <p>{review.comment}</p>}
        </div>
      </div>

      <div className='bg-neutral-100 rounded p-3 mt-6'>
        <h3 className='font-semibold'>{t('pages.sitterReviewReply.guidanceHeading')}</h3>
        <ul className='list-disc pl-5 mt-2 text-sm'>
          <li>{t('pages.sitterReviewReply.guidance1')}</li>
          <li>{t('pages.sitterReviewReply.guidance2')}</li>
          <li>
            {t('pages.sitterReviewReply.guidance3Prefix')}{' '}
            <a href='/review-guidelines' target='_blank' rel='noreferrer' className='underline'>
              {t('pages.sitterReviewReply.guidanceLink')}
            </a>
          </li>
        </ul>
      </div>

      <div className='mt-4'>
        <div className='text-xs text-right'>
          {t('pages.sitterReviewReply.charactersRemaining', { count: remaining })}
        </div>
        <textarea
          className='w-full border border-neutral-300 rounded p-2 min-h-[140px]'
          rows={6}
          maxLength={MAX_LEN}
          value={reply}
          onChange={(e): void => setReply(e.target.value)}
        />
      </div>

      <Button
        buttonType={ButtonType.primary}
        className='mt-4'
        disabled={saving}
        onClick={submit}
      >
        {t('pages.sitterReviewReply.submit')}
      </Button>
    </div>
  );
});
